import * as React from "react"
import { SettingsCardFooter } from "@modules/site/settings"

import { envVars as text } from "@modules/locales/default.js"
import { HelpLink } from "@modules/ui/components/HelpLink"
import {
  SiteEnvironmentVariables,
  SiteEnvironmentVariablesProps,
  SiteEnvironmentVariablesMode,
} from "@modules/site/envVars/components/SiteEnvironmentVariables"
import { SitePermissions } from "@modules/site/permissions"
import { Button, Card, CardHeader, CardSection } from "gatsby-interface"
import { MdEdit } from "react-icons/md"
import { useTracker } from "@modules/analytics"

export type EnvironmentVariablesCardProps = Pick<
  SiteEnvironmentVariablesProps,
  "siteId" | "organizationId" | "siteBuildsEnabled" | "uiSource"
> & {
  defaultEditMode?: boolean
  title?: React.ReactNode
  cardId?: string
  children?: React.ReactNode
}

export function EnvironmentVariablesCard({
  siteId,
  organizationId,
  siteBuildsEnabled = true,
  uiSource,
  defaultEditMode,
  title = text.headers.environmentVariables,
  cardId,
  children,
}: EnvironmentVariablesCardProps) {
  const { trackButtonClicked } = useTracker()
  const [mode, setMode] = React.useState<SiteEnvironmentVariablesMode>(
    defaultEditMode ? `EDIT` : `VIEW`
  )

  return (
    <Card id={cardId}>
      <CardHeader
        applyPaddingBottom={false}
        title={
          <React.Fragment>
            {title}{" "}
            <HelpLink
              href={`${window.location.origin}/docs/managing-environment-variables/`}
              aria-label={text.labels.helpLink}
              title={text.labels.helpLink}
              css={theme => ({ marginLeft: theme.space[3] })}
            />
          </React.Fragment>
        }
      >
        <SitePermissions id={siteId} resource="sites" action="edit">
          {mode !== `EDIT` && (
            <Button
              data-cy="edit-env-vars-button"
              onClick={() => {
                if (uiSource === `Site settings`) {
                  trackButtonClicked(`Edit environment variables`, {
                    organizationId: organizationId,
                    uiSource: `Site settings`,
                  })
                }
                if (uiSource === `New Site Wizard: setup`) {
                  trackButtonClicked(`Edit environment variables`, {
                    organizationId: organizationId,
                    uiSource: `New Site Wizard: setup`,
                  })
                }
                setMode(`EDIT`)
              }}
              size="L"
              variant="GHOST"
              rightIcon={<MdEdit />}
            >
              {text.actions.editVariables}
            </Button>
          )}
        </SitePermissions>
      </CardHeader>
      {children}
      <CardSection>
        <SiteEnvironmentVariables
          organizationId={organizationId}
          siteId={siteId}
          uiSource={uiSource}
          siteBuildsEnabled={siteBuildsEnabled}
          mode={mode}
          onModeChange={setMode}
          renderActions={actions => (
            <SettingsCardFooter variant="IN_SECTION">
              {actions}
            </SettingsCardFooter>
          )}
        />
      </CardSection>
    </Card>
  )
}
